"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { useDispatch } from "react-redux";

import { TopLoaderBar } from "@/components/ui/top-loader";
import { onSessionExpired } from "@/lib/session-expiry";
import { logout, sessionExpired, syncCurrentUserWithAPI } from "@/store/slices/authSlice";
import { AppDispatch, useAppSelector } from "@/store/store";

// How often to ping the profile endpoint while the tab is open (5 minutes).
const VALIDATION_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Gates every protected route, and is the only thing in the app that navigates
 * to the login page.
 *
 * The previous design had each failing request redirect itself from inside the
 * axios interceptor, while `proxy.ts` independently decided the user was still
 * logged in — two authorities disagreeing, which is what produced the bounce
 * between /auth/login and the landing page. Here the interceptor only reports,
 * this component decides, and it decides once.
 */
export function AuthGuard({ children }: { children: React.ReactNode }) {
  const dispatch = useDispatch<AppDispatch>();
  const router = useRouter();
  const status = useAppSelector((state) => state.auth.status);
  const lastSyncRef = useRef(0);

  // Bridge the axios interceptor into Redux. `onSessionExpired` is latched, so
  // a burst of 401s lands here exactly once.
  useEffect(() => {
    return onSessionExpired(() => {
      dispatch(sessionExpired());
      // Ask the API to drop the cookies too. Without this the cookies can
      // outlive the session we just gave up on, and `proxy.ts` would read them
      // as "logged in" and send the user straight back out of /auth/login.
      dispatch(logout({}));
    });
  }, [dispatch]);

  // The cookie is httpOnly, so the only way to learn whether it is still good
  // is to ask. Runs once per mount, while status is still "unknown" — except
  // `status` doesn't move off "unknown" until the request settles, so Strict
  // Mode's double-invoke in dev would otherwise fire it twice. The timestamp
  // ref is synchronous across both invocations, unlike Redux state, so it's
  // what actually closes the gap.
  useEffect(() => {
    const now = Date.now();
    if (status === "unknown" && now - lastSyncRef.current > 1000) {
      lastSyncRef.current = now;
      dispatch(syncCurrentUserWithAPI());
    }
  }, [status, dispatch]);

  // Re-verify the session when the user switches back to this tab. If the
  // token expired while the tab was hidden, the profile call will 401/403 and
  // the interceptor handles the redirect.
  useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState === "visible" && status === "authenticated") {
        dispatch(syncCurrentUserWithAPI());
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [status, dispatch]);

  // Periodic validation — catches expired tokens proactively instead of waiting
  // for the user to make a request that fails.
  useEffect(() => {
    if (status !== "authenticated") return;

    const interval = setInterval(() => {
      dispatch(syncCurrentUserWithAPI());
    }, VALIDATION_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [status, dispatch]);

  useEffect(() => {
    if (status === "unauthenticated") {
      // The `session=expired` marker is what makes this redirect final. Clearing
      // the cookies is an async round-trip, so for a moment the browser can still
      // hold a `session` cookie whose token has not visibly expired — and
      // proxy.ts would read that as "logged in" and bounce us back here. The
      // marker tells it we already know better, so it never second-guesses.
      //
      // replace(), not push() — a back button that returns to a dead page would
      // just bounce again.
      router.replace("/auth/login?session=expired");
    }
  }, [status, router]);

  // Withhold the page only while the answer is genuinely unknown — rendering
  // children early is what let a dying session pile up more 401s. A "degraded"
  // status still renders: proxy.ts already checked there is a session cookie, and
  // an unreachable profile endpoint is not grounds for logging anyone out.
  if (status === "unknown" || status === "unauthenticated") {
    return <TopLoaderBar active />;
  }

  return <>{children}</>;
}
